/**
 * Community User Profile — mirrors iOS ProfileSetupView (synced PUT fields).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Select, Switch, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { settingsEn } from '../../content/settings.en';
import {
  loadLocalUserPreferences,
  patchAppUserSettings,
} from '../../services/userPreferencesSyncService';
import {
  type CommunityUserProfile,
  type UserPrivacySettings,
  buildUserProfilePutBody,
  getCurrentUserProfile,
  putCurrentUserProfile,
} from '../../services/userProfileService';
import { getForgotPasswordUrl } from '../../utils/oauth';
import styles from './Settings.module.css';

const { TextArea } = Input;

const AGE_RANGES = ['18-25', '26-35', '36-50', '51-65', '65+'] as const;
const LOCATION_SHARING_KEY = 'wt_location_sharing_enabled';
const LOCATION_VISIBILITY_KEY = 'wt_location_visibility';

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [communityEnabled, setCommunityEnabled] = useState(
    () => loadLocalUserPreferences().appUserSettings.communityFeaturesEnabled
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<CommunityUserProfile | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [bio, setBio] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [privacy, setPrivacy] = useState<UserPrivacySettings>({
    shareTrips: false,
    shareLocation: false,
    allowDiscovery: false,
    showProfile: 'CONNECTIONS_ONLY',
  });
  const [locationSharingEnabled, setLocationSharingEnabled] = useState(() => {
    try {
      return localStorage.getItem(LOCATION_SHARING_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [locationVisibility, setLocationVisibility] = useState(() => {
    try {
      return localStorage.getItem(LOCATION_VISIBILITY_KEY) || 'CONNECTIONS_ONLY';
    } catch {
      return 'CONNECTIONS_ONLY';
    }
  });

  const applyProfile = useCallback((next: CommunityUserProfile) => {
    setProfile(next);
    setFirstName(next.firstName ?? '');
    setLastName(next.lastName ?? '');
    setBio(next.bio ?? '');
    setAgeRange(next.ageRange ?? '');
    setPrivacy({ ...next.privacySettings });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/signin?redirect=/settings/profile', { replace: true });
      return;
    }
    const enabled =
      loadLocalUserPreferences().appUserSettings.communityFeaturesEnabled;
    setCommunityEnabled(enabled);
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void getCurrentUserProfile()
      .then((p) => {
        if (!cancelled) applyProfile(p);
      })
      .catch(() => {
        if (!cancelled) message.error(settingsEn.userProfile.loadFailed);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, navigate, applyProfile]);

  const handleEnableCommunity = (): void => {
    patchAppUserSettings({ communityFeaturesEnabled: true });
    setCommunityEnabled(true);
    setLoading(true);
    void getCurrentUserProfile()
      .then((p) => applyProfile(p))
      .catch(() => message.error(settingsEn.userProfile.loadFailed))
      .finally(() => setLoading(false));
  };

  const handleSave = async (): Promise<void> => {
    if (!profile) return;
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      message.error(settingsEn.userProfile.nameRequired);
      return;
    }
    setSaving(true);
    try {
      const bioTrimmed = bio.trim();
      const ageTrimmed = ageRange.trim();
      const body = buildUserProfilePutBody(profile, {
        firstName: fn,
        lastName: ln,
        bio: bioTrimmed || null,
        ageRange: ageTrimmed || null,
        privacy,
      });
      const saved = await putCurrentUserProfile(body);
      applyProfile(saved);
      try {
        localStorage.setItem(
          LOCATION_SHARING_KEY,
          locationSharingEnabled ? 'true' : 'false'
        );
        localStorage.setItem(LOCATION_VISIBILITY_KEY, locationVisibility);
      } catch {
        // ignore
      }
      message.success(settingsEn.userProfile.saveSuccess);
    } catch {
      message.error(settingsEn.userProfile.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || (!isAuthenticated && loading)) {
    return (
      <div className={styles.settings}>
        <div className={styles.container}>
          <p className={styles.muted}>{settingsEn.userProfile.title}…</p>
        </div>
      </div>
    );
  }

  if (!communityEnabled) {
    return (
      <div className={styles.settings}>
        <div className={styles.container}>
          <header className={styles.header}>
            <Button
              type="link"
              className={styles.backLink}
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/settings', { replace: true })}
            >
              {settingsEn.back}
            </Button>
            <h1 className={styles.title}>{settingsEn.userProfile.title}</h1>
          </header>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {settingsEn.userProfile.communityRequired}
            </h2>
            <p className={styles.hint}>{settingsEn.userProfile.communityRequiredHint}</p>
            <div className={styles.actions}>
              <Button type="primary" onClick={handleEnableCommunity}>
                {settingsEn.community.enable}
              </Button>
              <Button onClick={() => navigate('/settings', { replace: true })}>
                {settingsEn.userProfile.openSettings}
              </Button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  const title =
    profile?.firstName || profile?.lastName
      ? settingsEn.userProfile.editTitle
      : settingsEn.userProfile.createTitle;

  return (
    <div className={styles.settings}>
      <div className={styles.container}>
        <header className={styles.header}>
          <Button
            type="link"
            className={styles.backLink}
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/settings', { replace: true })}
          >
            {settingsEn.back}
          </Button>
          <h1 className={styles.title}>{title}</h1>
        </header>

        {loading || !profile ? (
          <div className={styles.section}>
            <p className={styles.muted}>{settingsEn.userProfile.title}…</p>
          </div>
        ) : (
          <>
            <section className={styles.section} aria-labelledby="profile-basic">
              <h2 id="profile-basic" className={styles.sectionTitle}>
                {settingsEn.userProfile.title}
              </h2>
              <div className={styles.fieldBlock}>
                <div className={styles.label}>{settingsEn.userProfile.email}</div>
                <p className={styles.muted}>
                  {profile.email || user?.email || '—'}
                </p>
              </div>
              <div className={styles.fieldBlock}>
                <div className={styles.label}>{settingsEn.userProfile.firstName}</div>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  aria-label={settingsEn.userProfile.firstName}
                  autoComplete="given-name"
                />
              </div>
              <div className={styles.fieldBlock}>
                <div className={styles.label}>{settingsEn.userProfile.lastName}</div>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  aria-label={settingsEn.userProfile.lastName}
                  autoComplete="family-name"
                />
              </div>
              <p className={styles.hint}>
                {settingsEn.userProfile.travelStyleHint}{' '}
                <Link to="/settings/traveler-preferences">
                  {settingsEn.userSettings.travelerPrefs}
                </Link>
              </p>
            </section>

            <section className={styles.section} aria-labelledby="about-you">
              <h2 id="about-you" className={styles.sectionTitle}>
                {settingsEn.userProfile.aboutYou}
              </h2>
              <div className={styles.fieldBlock}>
                <div className={styles.label}>{settingsEn.userProfile.bio}</div>
                <TextArea
                  rows={4}
                  value={bio}
                  placeholder={settingsEn.userProfile.bioPlaceholder}
                  onChange={(e) => setBio(e.target.value)}
                  aria-label={settingsEn.userProfile.bio}
                />
              </div>
              {profile.homeCity ? (
                <div className={styles.fieldBlock}>
                  <div className={styles.label}>{settingsEn.userProfile.homeCity}</div>
                  <p className={styles.inboundAddress}>{profile.homeCity}</p>
                  <p className={styles.hint}>{settingsEn.userProfile.homeCityHint}</p>
                </div>
              ) : null}
              <div className={styles.fieldBlock}>
                <div className={styles.label}>{settingsEn.userProfile.ageRange}</div>
                <Select
                  className={styles.fullControl}
                  allowClear
                  placeholder={settingsEn.userProfile.ageNotSpecified}
                  value={ageRange || undefined}
                  onChange={(v) => setAgeRange(v ? String(v) : '')}
                  options={AGE_RANGES.map((r) => ({ value: r, label: r }))}
                  aria-label={settingsEn.userProfile.ageRange}
                />
              </div>
            </section>

            <section className={styles.section} aria-labelledby="community-privacy">
              <h2 id="community-privacy" className={styles.sectionTitle}>
                {settingsEn.userProfile.communityFeatures}
              </h2>
              {(
                [
                  ['shareTrips', settingsEn.userProfile.shareTrips, settingsEn.userProfile.shareTripsHint],
                  [
                    'shareLocation',
                    settingsEn.userProfile.shareLocation,
                    settingsEn.userProfile.shareLocationHint,
                  ],
                  [
                    'allowDiscovery',
                    settingsEn.userProfile.allowDiscovery,
                    settingsEn.userProfile.allowDiscoveryHint,
                  ],
                ] as const
              ).map(([key, label, hint]) => (
                <div className={styles.row} key={key}>
                  <div className={styles.label}>
                    <div>{label}</div>
                    <p className={styles.hint}>{hint}</p>
                  </div>
                  <Switch
                    checked={privacy[key]}
                    onChange={(checked) =>
                      setPrivacy((prev) => ({ ...prev, [key]: checked }))
                    }
                    aria-label={label}
                  />
                </div>
              ))}
            </section>

            <section className={styles.section} aria-labelledby="visibility">
              <h2 id="visibility" className={styles.sectionTitle}>
                {settingsEn.userProfile.profileVisibility}
              </h2>
              <div className={styles.fieldBlock}>
                <div className={styles.label}>{settingsEn.userProfile.showProfileTo}</div>
                <Select
                  className={styles.fullControl}
                  value={privacy.showProfile}
                  onChange={(v) =>
                    setPrivacy((prev) => ({ ...prev, showProfile: String(v) }))
                  }
                  options={[
                    {
                      value: 'PUBLIC',
                      label: settingsEn.userProfile.visibilityEveryone,
                    },
                    {
                      value: 'CONNECTIONS_ONLY',
                      label: settingsEn.userProfile.visibilityConnections,
                    },
                    {
                      value: 'PRIVATE',
                      label: settingsEn.userProfile.visibilityNoOne,
                    },
                  ]}
                  aria-label={settingsEn.userProfile.showProfileTo}
                />
              </div>
            </section>

            <section className={styles.section} aria-labelledby="location-sharing">
              <h2 id="location-sharing" className={styles.sectionTitle}>
                {settingsEn.userProfile.locationSharing}
              </h2>
              <div className={styles.row}>
                <div className={styles.label}>
                  <div>{settingsEn.userProfile.locationSharingEnable}</div>
                  <p className={styles.hint}>{settingsEn.userProfile.locationSharingHint}</p>
                </div>
                <Switch
                  checked={locationSharingEnabled}
                  onChange={setLocationSharingEnabled}
                  aria-label={settingsEn.userProfile.locationSharingEnable}
                />
              </div>
              {locationSharingEnabled ? (
                <div className={styles.fieldBlock}>
                  <div className={styles.label}>
                    {settingsEn.userProfile.locationVisibility}
                  </div>
                  <Select
                    className={styles.fullControl}
                    value={locationVisibility}
                    onChange={(v) => setLocationVisibility(String(v))}
                    options={[
                      {
                        value: 'CONNECTIONS_ONLY',
                        label: settingsEn.userProfile.visibilityConnections,
                      },
                      {
                        value: 'PUBLIC',
                        label: settingsEn.userProfile.visibilityEveryone,
                      },
                      {
                        value: 'PRIVATE',
                        label: settingsEn.userProfile.visibilityNoOne,
                      },
                    ]}
                    aria-label={settingsEn.userProfile.locationVisibility}
                  />
                  <p className={styles.hint}>
                    {settingsEn.userProfile.locationVisibilityHint}
                  </p>
                </div>
              ) : null}
            </section>

            <section className={styles.section} aria-labelledby="security">
              <h2 id="security" className={styles.sectionTitle}>
                {settingsEn.userProfile.security}
              </h2>
              <a
                className={styles.navLink}
                href={getForgotPasswordUrl()}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>{settingsEn.userProfile.changePassword}</span>
              </a>
              <p className={styles.hint}>{settingsEn.userProfile.changePasswordHint}</p>
            </section>

            <section className={styles.section} aria-labelledby="blocked">
              <h2 id="blocked" className={styles.sectionTitle}>
                {settingsEn.userProfile.blockedUsers}
              </h2>
              <p className={styles.comingSoon}>
                {settingsEn.userProfile.blockedComingSoon}
              </p>
            </section>

            <div className={styles.actions}>
              <Button
                type="primary"
                size="large"
                loading={saving}
                disabled={saving || !firstName.trim() || !lastName.trim()}
                onClick={() => void handleSave()}
              >
                {settingsEn.userProfile.save}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
